import { Injectable, inject } from '@angular/core';
import { CurrencyPipe } from '@angular/common';
import { OrderItemRowTypeEnumResponseDto } from '@core/services/apiclient';
import { TreeListRowData } from '@app/orders/models/orders.model';
import { TranslateService } from '@ngx-translate/core';

export type RefundSummaryItem = {
  type: 'subscription-group' | 'subscription-header' | 'subscription-item' | 'folder';
  label: string;
};

@Injectable({
  providedIn: 'root',
})
export class RefundSummaryBuilderService {
  private readonly currencyPipe = inject(CurrencyPipe);
  private readonly translate = inject(TranslateService);

  buildSummaryItems(items: TreeListRowData[]): RefundSummaryItem[] {
    const summary: RefundSummaryItem[] = [];

    const subscriptionGroups = items.filter(
      (row) => row?.orderItemRowType === OrderItemRowTypeEnumResponseDto.SubscriptionGroup,
    );
    subscriptionGroups.forEach((row) => {
      const fullFormulaLabel = this.translate.instant('orders.refundSummary.fullFormula', {
        id: row.id,
        orderId: row.orderId,
        label: row.label,
        amount: this.formatAmount(this.getRowAmount(row)),
      });
      summary.push({
        type: 'subscription-group',
        label: fullFormulaLabel,
      });
    });

    const subscriptionItems = items.filter(
      (row) => row?.orderItemRowType === OrderItemRowTypeEnumResponseDto.Subscription,
    );
    const groups = new Map<number, TreeListRowData[]>();
    subscriptionItems.forEach((row) => {
      const parentId = row.parentId ?? row.subscriptionOrderId;
      if (!parentId) {
        const folderLabel = this.translate.instant('orders.refundSummary.folder', {
          id: row.id,
          orderId: row.orderId,
          label: row.label,
          amount: this.formatAmount(this.getRowAmount(row)),
        });
        summary.push({
          type: 'subscription-item',
          label: folderLabel,
        });
        return;
      }
      const existing = groups.get(parentId) ?? [];
      groups.set(parentId, [...existing, row]);
    });

    groups.forEach((rows, parentId) => {
      const totalAmount = rows.reduce((acc, row) => acc + this.getRowAmount(row), 0);
      const orderId = rows[0]?.orderId;
      const parentLabel = rows[0]?.parentLabel ?? rows[0]?.label ?? '';
      const formulaLabel = this.translate.instant('orders.refundSummary.formula', {
        id: parentId,
        orderId: orderId,
        label: parentLabel,
        amount: this.formatAmount(totalAmount),
      });
      summary.push({
        type: 'subscription-header',
        label: formulaLabel,
      });
      rows.forEach((row) => {
        const folderLabel = this.translate.instant('orders.refundSummary.folder', {
          id: row.id,
          orderId: row.orderId,
          label: row.label,
          amount: this.formatAmount(this.getRowAmount(row)),
        });
        summary.push({
          type: 'subscription-item',
          label: folderLabel,
        });
      });
    });

    const folders = items.filter(
      (row) =>
        row?.orderItemRowType !== OrderItemRowTypeEnumResponseDto.SubscriptionGroup &&
        row?.orderItemRowType !== OrderItemRowTypeEnumResponseDto.Subscription,
    );
    folders.forEach((row) => {
      const folderLabel = this.translate.instant('orders.refundSummary.folder', {
        id: row.id,
        orderId: row.orderId,
        label: row.label,
        amount: this.formatAmount(this.getRowAmount(row)),
      });
      summary.push({
        type: 'folder',
        label: folderLabel,
      });
    });

    return summary;
  }

  calculateTotalAmount(items: TreeListRowData[]): number {
    return (items ?? []).reduce((acc, row) => acc + this.getRowAmount(row), 0);
  }

  private getRowAmount(row: TreeListRowData): number {
    return row?.amount ?? 0;
  }

  private formatAmount(amount: number): string {
    return this.currencyPipe.transform(amount, 'EUR', 'symbol', '0.2-2', 'fr') ?? '';
  }
}
